import { useEffect, useMemo, useState } from 'react'
import {
  Plus,
  KanbanSquare,
  ChevronLeft,
  ChevronRight,
  ArrowRightLeft,
  AlertCircle,
  ArrowUp,
  Minus,
  ArrowDown,
  Calendar,
  X,
} from 'lucide-react'
import {
  useProjectStore,
  TASK_STATUS_ORDER,
  type Task,
  type TaskStatus,
  type TaskPriority,
} from '../../store/projectStore'
import { usePermissions } from '../../hooks/usePermissions'
import TaskDetailModal from './TaskDetailModal'

const COLUMN_LABELS: Record<TaskStatus, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
  blocked: 'Blocked',
}

const COLUMN_DOT: Record<TaskStatus, string> = {
  todo: 'bg-gray-400',
  in_progress: 'bg-blue-500',
  review: 'bg-purple-500',
  done: 'bg-green-500',
  blocked: 'bg-red-500',
}

const PRIORITY_BADGE: Record<TaskPriority, { label: string; icon: React.ElementType; className: string }> = {
  urgent: { label: 'Urgent', icon: AlertCircle, className: 'text-red-700 bg-red-100' },
  high:   { label: 'High',   icon: ArrowUp,     className: 'text-orange-700 bg-orange-100' },
  medium: { label: 'Med',    icon: Minus,       className: 'text-gray-700 bg-gray-100' },
  low:    { label: 'Low',    icon: ArrowDown,   className: 'text-blue-700 bg-blue-100' },
}

function isOverdue(date?: string | null) {
  if (!date) return false
  return new Date(date) < new Date(new Date().toDateString())
}

interface MobileKanbanBoardProps {
  projectId: number
}

interface MobileTaskCardProps {
  task: Task
  onOpen: (task: Task) => void
  onMove: (task: Task) => void
}

function MobileTaskCard({ task, onOpen, onMove }: MobileTaskCardProps) {
  const prio = PRIORITY_BADGE[task.priority]
  const PrioIcon = prio.icon
  const overdue = isOverdue(task.due_date) && task.status !== 'done'

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm active:bg-gray-50 space-y-2.5">
      <button onClick={() => onOpen(task)} className="block w-full text-left space-y-2.5">
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-base font-semibold text-gray-900 flex-1">{task.title}</h4>
          <span className={`inline-flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-medium ${prio.className}`}>
            <PrioIcon className="h-3 w-3" />
            {prio.label}
          </span>
        </div>

        {task.completion_percent > 0 && (
          <div className="space-y-1">
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full bg-primary-500"
                style={{ width: `${Math.min(100, task.completion_percent)}%` }}
              />
            </div>
            <p className="text-[11px] text-gray-500">{task.completion_percent}% complete</p>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-gray-500">
          {task.due_date ? (
            <div className={`inline-flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}>
              <Calendar className="h-3.5 w-3.5" />
              {new Date(task.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              {overdue && ' (overdue)'}
            </div>
          ) : <span />}
          {task.assignee_name && (
            <div className="inline-flex items-center gap-1.5">
              <span className="h-6 w-6 rounded-full bg-primary-100 text-primary-700 text-[11px] font-medium flex items-center justify-center">
                {task.assignee_name.charAt(0).toUpperCase()}
              </span>
              <span className="truncate max-w-[100px]">{task.assignee_name}</span>
            </div>
          )}
        </div>
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onMove(task) }}
        className="flex items-center justify-center gap-1.5 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700 active:bg-gray-100"
      >
        <ArrowRightLeft className="h-3.5 w-3.5" />
        Move
      </button>
    </div>
  )
}

interface MoveSheetProps {
  task: Task | null
  onClose: () => void
  onPick: (status: TaskStatus) => void
}

function MoveSheet({ task, onClose, onPick }: MoveSheetProps) {
  if (!task) return null
  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative w-full rounded-t-2xl bg-white shadow-2xl pb-safe" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center pt-2">
          <span className="h-1 w-10 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-5 py-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold truncate">Move task</h2>
            <p className="text-xs text-gray-500 truncate">{task.title}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <ul className="divide-y divide-gray-100">
          {TASK_STATUS_ORDER.map((s) => {
            const isCurrent = s === task.status
            return (
              <li key={s}>
                <button
                  onClick={() => onPick(s)}
                  disabled={isCurrent}
                  className={`flex w-full items-center gap-3 px-5 py-4 active:bg-gray-50 disabled:opacity-50 ${isCurrent ? 'bg-gray-50' : ''}`}
                >
                  <span className={`h-2.5 w-2.5 rounded-full ${COLUMN_DOT[s]}`} />
                  <span className="text-sm font-medium text-gray-800 flex-1 text-left">{COLUMN_LABELS[s]}</span>
                  {isCurrent && <span className="text-[11px] text-gray-500">current</span>}
                </button>
              </li>
            )
          })}
        </ul>
        <div className="h-6" />
      </div>
    </div>
  )
}

interface QuickAddSheetProps {
  status: TaskStatus | null
  onClose: () => void
  onSubmit: (title: string) => Promise<void>
}

function QuickAddSheet({ status, onClose, onSubmit }: QuickAddSheetProps) {
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  if (!status) return null

  const handle = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setBusy(true)
    try {
      await onSubmit(title.trim())
      setTitle('')
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <form onSubmit={handle} className="relative w-full rounded-t-2xl bg-white shadow-2xl pb-safe" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center pt-2">
          <span className="h-1 w-10 rounded-full bg-gray-300" />
        </div>
        <div className="px-5 py-3">
          <h2 className="text-base font-semibold">New task in {COLUMN_LABELS[status]}</h2>
        </div>
        <div className="px-5 pb-3">
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to be done?"
            className="w-full rounded-lg border border-gray-300 px-3 py-3 text-base focus:border-primary-500 focus:outline-none"
          />
        </div>
        <div className="flex gap-2 px-5 pb-5">
          <button type="button" onClick={onClose}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-3 text-sm font-medium hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={busy || !title.trim()}
                  className="flex-1 rounded-lg bg-primary-600 px-3 py-3 text-sm font-medium text-white active:bg-primary-700 disabled:opacity-50">
            {busy ? 'Adding…' : 'Add task'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default function MobileKanbanBoard({ projectId }: MobileKanbanBoardProps) {
  const { board, fetchBoard, moveTask, createTask } = useProjectStore()
  const { can } = usePermissions()
  const [colIdx, setColIdx] = useState(0)
  const [openTask, setOpenTask] = useState<Task | null>(null)
  const [moveTaskTarget, setMoveTaskTarget] = useState<Task | null>(null)
  const [quickAddStatus, setQuickAddStatus] = useState<TaskStatus | null>(null)
  const [touchStartX, setTouchStartX] = useState<number | null>(null)

  useEffect(() => {
    fetchBoard(projectId)
  }, [projectId, fetchBoard])

  const columns: Record<TaskStatus, Task[]> = useMemo(() => {
    const empty = TASK_STATUS_ORDER.reduce(
      (acc, s) => ({ ...acc, [s]: [] as Task[] }),
      {} as Record<TaskStatus, Task[]>
    )
    return { ...empty, ...(board?.columns || {}) }
  }, [board])

  const currentStatus = TASK_STATUS_ORDER[colIdx]
  const currentTasks = columns[currentStatus] || []

  const canCreate = can('admin_project_monitoring', 'create') || can('pwa_project_monitoring', 'create')
  const canEdit = can('admin_project_monitoring', 'edit') || can('pwa_project_monitoring', 'edit')
  const canDelete = can('admin_project_monitoring', 'delete') || can('pwa_project_monitoring', 'delete')

  const goPrev = () => setColIdx((i) => Math.max(0, i - 1))
  const goNext = () => setColIdx((i) => Math.min(TASK_STATUS_ORDER.length - 1, i + 1))

  const handleMovePick = async (newStatus: TaskStatus) => {
    if (!moveTaskTarget || newStatus === moveTaskTarget.status) return
    const colTasks = columns[newStatus]
    const maxPos = colTasks.length ? Math.max(...colTasks.map((t) => t.position || 0)) : 0
    try {
      await moveTask(moveTaskTarget.id, newStatus, maxPos + 10)
    } catch {
      await fetchBoard(projectId)
    }
    setMoveTaskTarget(null)
  }

  const handleQuickAdd = async (title: string) => {
    if (!quickAddStatus) return
    await createTask(projectId, { title, status: quickAddStatus })
  }

  const onTouchStart = (e: React.TouchEvent) => setTouchStartX(e.touches[0].clientX)
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX == null) return
    const dx = e.changedTouches[0].clientX - touchStartX
    if (Math.abs(dx) > 60) {
      if (dx < 0) goNext()
      else goPrev()
    }
    setTouchStartX(null)
  }

  if (!board) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
        <KanbanSquare className="mx-auto h-10 w-10 text-gray-400 animate-pulse" />
        <p className="mt-3 text-sm text-gray-500">Loading board…</p>
      </div>
    )
  }

  return (
    <div className="px-3 pb-40">
      {/* Status tabs — scrollable horizontally */}
      <div className="flex gap-2 overflow-x-auto py-2 -mx-3 px-3">
        {TASK_STATUS_ORDER.map((s, i) => {
          const count = columns[s].length
          const active = i === colIdx
          return (
            <button
              key={s}
              onClick={() => setColIdx(i)}
              className={`shrink-0 inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm transition-colors ${
                active ? 'bg-primary-600 text-white shadow-sm' : 'bg-white border border-gray-200 text-gray-700'
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${active ? 'bg-white/80' : COLUMN_DOT[s]}`} />
              <span className="font-medium">{COLUMN_LABELS[s]}</span>
              <span className={`text-[11px] rounded-full px-1.5 py-0.5 ${active ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Pager header */}
      <div className="mt-4 flex items-center justify-between rounded-lg bg-white border border-gray-200 px-3 py-2.5">
        <button
          onClick={goPrev}
          disabled={colIdx === 0}
          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"
          aria-label="Previous column"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="text-center">
          <p className="text-[11px] text-gray-500">Column {colIdx + 1} of {TASK_STATUS_ORDER.length}</p>
          <p className="text-sm font-semibold text-gray-900">{COLUMN_LABELS[currentStatus]}</p>
        </div>
        <button
          onClick={goNext}
          disabled={colIdx === TASK_STATUS_ORDER.length - 1}
          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"
          aria-label="Next column"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Column dot indicators — placed BELOW the pager so it doesn't crowd the cards */}
      <div className="mt-3 flex justify-center gap-1.5">
        {TASK_STATUS_ORDER.map((s, i) => (
          <button
            key={s}
            onClick={() => setColIdx(i)}
            className={`h-1.5 rounded-full transition-all ${i === colIdx ? 'w-6 bg-primary-600' : 'w-1.5 bg-gray-300'}`}
            aria-label={`Go to ${COLUMN_LABELS[s]}`}
          />
        ))}
      </div>

      {/* Tasks list for current column with swipe gestures */}
      <div
        className="mt-5 space-y-3 min-h-[200px]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {currentTasks.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-white p-8 text-center">
            <p className="text-sm text-gray-500">No tasks in {COLUMN_LABELS[currentStatus]}</p>
            {canCreate && (
              <button
                onClick={() => setQuickAddStatus(currentStatus)}
                className="mt-3 inline-flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white"
              >
                <Plus className="h-4 w-4" />
                Add task
              </button>
            )}
          </div>
        ) : (
          currentTasks.map((t) => (
            <MobileTaskCard
              key={t.id}
              task={t}
              onOpen={setOpenTask}
              onMove={canEdit ? setMoveTaskTarget : () => {}}
            />
          ))
        )}
      </div>

      {/* FAB */}
      {canCreate && (
        <button
          onClick={() => setQuickAddStatus(currentStatus)}
          className="fixed bottom-24 right-4 z-30 inline-flex items-center justify-center rounded-full bg-primary-600 p-4 text-white shadow-lg active:bg-primary-700"
          aria-label={`Add task to ${COLUMN_LABELS[currentStatus]}`}
        >
          <Plus className="h-6 w-6" />
        </button>
      )}

      <MoveSheet
        task={moveTaskTarget}
        onClose={() => setMoveTaskTarget(null)}
        onPick={handleMovePick}
      />

      <QuickAddSheet
        status={quickAddStatus}
        onClose={() => setQuickAddStatus(null)}
        onSubmit={handleQuickAdd}
      />

      <TaskDetailModal
        isOpen={openTask !== null}
        task={openTask}
        projectId={projectId}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={() => setOpenTask(null)}
      />
    </div>
  )
}
